import { PmBankAccountSearchResponseDto, Configuration, InvoiceBankAccountConfigDto, InvoiceSearchDto, PmsApi, ReadInvoiceDto, InvoiceFileType, PricingQuoteDetailsDto } from '@inosio/payments-sdk';
import { AxiosResponse } from 'axios';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Stream } from 'stream';
import { IpaUserExtended, selectCurrentUser } from '../../../shared/sagas/auth/authSlice';
import {
  setAvailableBankAccountsError,
  getAvailableBankAccount,
  getUpdateInvoiceBankAccount,
  getPricingOption,
  setUpdateInvoiceBankAccountError,
  setAvailableBankAccountsResponse,
} from './billingSlice';
import { setPricingOptionResponse, setPricingOptionError, getInvoices, getInvoiceFile, setInvoicesResponse, setInvoicesError, setInvoiceFileError } from './billingSlice';

const { REACT_APP_IPA_BASE_PATH } = process.env;

export function requestGetAvailableBankAccounts(accessToken: string, pmUuid: string, fundableOnly?: boolean) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });

  const ipaPmsApi = new PmsApi(apiConfig);
  return ipaPmsApi.fetchAvailableBankAccounts(pmUuid, fundableOnly);
}

export function requestUpdateInvoiceBankAccount(accessToken: string, pmUuid: string, invoiceBankAccountConfigDto: InvoiceBankAccountConfigDto) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });

  const ipaPmsApi = new PmsApi(apiConfig);
  return ipaPmsApi.setInvoiceBankAccount(pmUuid, invoiceBankAccountConfigDto);
}

export function requestGetPricingOption(accessToken: string, pmUuid: string) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });

  const ipaPmsApi = new PmsApi(apiConfig);
  return ipaPmsApi.fetchProductsPurchased(pmUuid);
}

export function requestGetInvoices(accessToken: string, pmUuid: string, invoiceSearchDto: InvoiceSearchDto) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });

  const ipaPmsApi = new PmsApi(apiConfig);
  return ipaPmsApi.searchInvoicesByPM(pmUuid, invoiceSearchDto);
}

export function requestDownloadInvoiceFile(accessToken: string, pmUuid: string, invoiceId: number, invoiceFileType: InvoiceFileType) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });
  const ipaPmsApi = new PmsApi(apiConfig);
  const responseType = 'arraybuffer';
  const contentDisposition = 'application/pdf';
  const headerParams = {
    'Content-Disposition': contentDisposition,
  };
  return ipaPmsApi.exportInvoiceDetails(pmUuid, invoiceFileType, invoiceId, { responseType, headers: headerParams });
}

export function* handleGetAvailableBankAccounts(action: any) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<PmBankAccountSearchResponseDto[]> = yield call(requestGetAvailableBankAccounts, currentUser.accessToken, currentUser.currentProfile.uuid, action?.payload?.fundableOnly);

    const { data } = response;
    yield put(setAvailableBankAccountsResponse(data || []));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setAvailableBankAccountsError({ ...error?.response?.data }));
    } else {
      yield put(setAvailableBankAccountsError({ message: error?.message }));
    }
  }
}
export function* handleGetUpdateInvoiceBankAccount(action: { type: typeof getUpdateInvoiceBankAccount; payload: InvoiceBankAccountConfigDto }) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    yield call(requestUpdateInvoiceBankAccount, currentUser.accessToken, currentUser.currentProfile.uuid, action.payload);
  } catch (error: any) {
    yield put(setUpdateInvoiceBankAccountError({ ...error?.response?.data }));
  }
}
export function* handleGetPricingOption(action: { type: typeof getPricingOption }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<PricingQuoteDetailsDto> = yield call(requestGetPricingOption, currentUser.accessToken, currentUser.currentProfile.uuid);

    const { data } = response;
    yield put(setPricingOptionResponse(data));
  } catch (error: any) {
    if (error?.response?.data) {
      yield put(setPricingOptionError({ ...error?.response?.data }));
    } else {
      yield put(setPricingOptionError({ message: error?.message }));
    }
  }
}

export function* handleGetInvoices(action: { type: typeof getInvoices; payload: InvoiceSearchDto }) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<ReadInvoiceDto> = yield call(requestGetInvoices, currentUser.accessToken, currentUser.currentProfile.uuid, action.payload);

    const { data } = response;
    yield put(setInvoicesResponse(data));
  } catch (error: any) {
    yield put(setInvoicesError({ ...error?.response?.data }));
  }
}

export function* handleDownloadInvoiceFile(action: { type: typeof getInvoiceFile; invoiceId: number; invoiceFileType: InvoiceFileType }) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<Stream> = yield call(requestDownloadInvoiceFile, currentUser.accessToken, currentUser.currentProfile.uuid, action.invoiceId, action.invoiceFileType);

    // download stream
    let fileName = 'invoice.pdf'; // falllback
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(response.headers['content-disposition']);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, '');
    }

    const url = window.URL.createObjectURL(new Blob([response.data as unknown as BlobPart], { type: response.headers['content-type'] }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}`);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
  } catch (error: any) {
    yield put(setInvoiceFileError({ ...error?.response?.data }));
  }
}

export default function* billingSaga() {
  yield takeLatest(getUpdateInvoiceBankAccount.type, handleGetUpdateInvoiceBankAccount);
  yield takeLatest(getPricingOption.type, handleGetPricingOption);
  yield takeLatest(getAvailableBankAccount.type, handleGetAvailableBankAccounts);
  yield takeLatest(getInvoices.type, handleGetInvoices);
  yield takeEvery(getInvoiceFile.type, handleDownloadInvoiceFile);
}
